// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the agent's question in a DM (design/agents-side-bar r2-q3), a `Konvo:agent_question`
// card the thread's owner posted to a colleague. The asked person picks an option or writes an
// answer, and "Send to the agent" answers it from the owner's machine. Afterwards the card says
// what happened: answered, the owner answered first, or the agent moved on. The owner sees the
// same card read-only with a way into the thread. Anyone else (or a failed read) gets the plain
// card the embed carries, which is also what older apps show.

import {Routes} from '@app/app/Routes';
import styles from '@app/features/konvo_agents/components/AgentAsk.module.css';
import {RunningRing} from '@app/features/konvo_agents/components/AgentBits';
import {ProviderLogo} from '@app/features/konvo_agents/components/AgentBrand';
import questionStyles from '@app/features/konvo_agents/components/AgentQuestion.module.css';
import {
	type Draft,
	draftAnswers,
	EMPTY_DRAFT,
	optionValue,
	realHeader,
} from '@app/features/konvo_agents/components/AgentQuestionCard';
import {agentErrorMessage} from '@app/features/konvo_agents/components/AgentsCopy';
import AgentAsks from '@app/features/konvo_agents/state/AgentAsks';
import {firstName} from '@app/features/konvo_agents/state/AgentAsksModel';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {fitComposerHeight} from '@app/features/platform/utils/ComposerHeight';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import type {AgentAsk, AgentQuestion} from '@konvo/schema/src/domains/konvo/Agents';
import {Trans, useLingui} from '@lingui/react/macro';
import {IconCheck, IconCircleCheck, IconCircleOff, IconClockHour4} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {type ReactNode, useEffect, useState} from 'react';

/** One question: its options as numbered rows, and a written answer when the agent allows one. */
function QuestionBlock({
	question,
	draft,
	onChange,
	editable,
	showWritten,
	busy,
}: {
	question: AgentQuestion;
	draft: Draft;
	onChange: (next: Draft) => void;
	editable: boolean;
	/** The written answer sits in this block (several questions); one question has it in the foot. */
	showWritten: boolean;
	busy: boolean;
}) {
	const {t} = useLingui();
	const header = realHeader(question.header);
	const pick = (value: string) => {
		if (question.multiSelect) {
			const selected = draft.selected.includes(value)
				? draft.selected.filter((v) => v !== value)
				: [...draft.selected, value];
			onChange({selected, custom: ''});
		} else onChange({selected: [value], custom: ''});
	};
	return (
		<div className={styles.cardQuestion} data-flx="konvo-agents.ask-card.question">
			{header && <span className={questionStyles.kicker}>{header}</span>}
			<p className={questionStyles.question}>{question.question}</p>
			{question.multiSelect && question.options.length > 0 && editable && (
				<span className={questionStyles.hint}>
					<Trans>Pick one or more.</Trans>
				</span>
			)}
			{question.options.length > 0 && (
				<div className={questionStyles.options} role="group" aria-label={question.question}>
					{question.options.map((option, i) => {
						const value = optionValue(option);
						const on = draft.selected.includes(value) && !draft.custom.trim();
						return (
							<button
								key={value}
								type="button"
								aria-pressed={on}
								className={clsx(questionStyles.option, on && questionStyles.optionOn)}
								onClick={() => pick(value)}
								disabled={!editable || busy}
								data-flx="konvo-agents.ask-card.option"
							>
								<span className={clsx(questionStyles.key, question.multiSelect && questionStyles.keySquare)}>
									{on ? <IconCheck size={13} stroke={2.4} /> : i + 1}
								</span>
								<span className={questionStyles.optionText}>
									<span className={questionStyles.optionLabel}>{option.label}</span>
									{option.description && <span className={questionStyles.optionDescription}>{option.description}</span>}
								</span>
							</button>
						);
					})}
				</div>
			)}
			{showWritten && editable && question.allowCustomAnswer && (
				<WrittenAnswer
					question={question}
					draft={draft}
					onChange={onChange}
					busy={busy}
					placeholder={t`Or write an answer…`}
				/>
			)}
		</div>
	);
}

function WrittenAnswer({
	question,
	draft,
	onChange,
	busy,
	placeholder,
}: {
	question: AgentQuestion;
	draft: Draft;
	onChange: (next: Draft) => void;
	busy: boolean;
	placeholder: string;
}) {
	const {t} = useLingui();
	return (
		<textarea
			className={styles.cardWritten}
			rows={1}
			value={draft.custom}
			placeholder={question.options.length === 0 ? t`Write your answer…` : placeholder}
			aria-label={t`Your answer`}
			onChange={(event) => {
				const custom = event.target.value;
				onChange({selected: custom.trim() ? [] : draft.selected, custom});
				fitComposerHeight(event.target);
			}}
			disabled={busy}
			data-flx="konvo-agents.ask-card.written"
		/>
	);
}

function StatusLine({ask, role}: {ask: AgentAsk; role: 'owner' | 'person'}) {
	const owner = firstName(ask.ownerName);
	const person = firstName(ask.personName);
	// Several answers are listed above the line; one fits in it.
	const summary = ask.questions.length === 1 ? (ask.answer?.[0]?.answer ?? '') : '';
	let icon: ReactNode = <IconClockHour4 size={16} stroke={1.8} />;
	let text: ReactNode;
	switch (ask.status) {
		case 'answered':
			icon = <IconCircleCheck size={16} stroke={1.9} />;
			text =
				role === 'owner' ? (
					summary ? (
						<Trans comment="Agents: the colleague answered the question, e.g. Lerato answered · Safari.">
							{person} answered · {summary}
						</Trans>
					) : (
						<Trans comment="Agents: the colleague answered the agent's questions, e.g. Lerato answered.">
							{person} answered
						</Trans>
					)
				) : summary ? (
					<Trans comment="Agents: you answered the agent's question, e.g. Answered · Safari.">
						Answered · {summary}
					</Trans>
				) : (
					<Trans comment="Agents: you answered the agent's questions.">Answered</Trans>
				);
			break;
		case 'owner':
			icon = <IconCircleCheck size={16} stroke={1.9} />;
			text =
				role === 'owner' ? (
					<Trans comment="Agents: the owner answered the question themselves before the colleague did.">
						You answered it yourself
					</Trans>
				) : (
					<Trans comment="Agents: the person who asked answered it themselves first, e.g. JP answered it.">
						{owner} answered it
					</Trans>
				);
			break;
		case 'closed':
			icon = <IconCircleOff size={16} stroke={1.8} />;
			text = (
				<Trans comment="Agents: the agent's question can no longer be answered.">
					No longer open. It was answered, or the agent moved on.
				</Trans>
			);
			break;
		default:
			text =
				role === 'owner' ? (
					<Trans comment="Agents: the owner's view of a question out with a colleague, e.g. Waiting for Lerato.">
						Waiting for {person}
					</Trans>
				) : null;
	}
	if (!text) return null;
	return (
		<span
			className={clsx(styles.cardStatus, ask.status === 'answered' && styles.cardStatusDone)}
			data-flx="konvo-agents.ask-card.status"
			data-status={ask.status}
		>
			{icon}
			{text}
		</span>
	);
}

export const AgentAskCard = observer(
	({askId, statusKey, fallback}: {askId: string; statusKey: string; fallback: ReactNode}) => {
		const {i18n, t} = useLingui();
		const phone = MobileLayout.platformMobileDetected || MobileLayout.enabled;
		const [drafts, setDrafts] = useState<Record<string, Draft>>({});
		const [sending, setSending] = useState(false);
		// The card's message is redrawn when the ask changes: read it again then.
		useEffect(() => {
			void AgentAsks.loadCard(askId);
		}, [askId, statusKey]);
		const state = AgentAsks.card(askId);
		const view = state?.view ?? null;
		if (!view) return state?.failed ? fallback : <div className={styles.card} aria-busy="true" />;
		const {ask, role} = view;
		const editable = role === 'person' && ask.status === 'waiting';
		const single = ask.questions.length === 1;
		const answers = draftAnswers(ask.questions, drafts);
		const owner = firstName(ask.ownerName);
		const person = firstName(ask.personName);
		const meta = [ask.projectTitle, ask.threadTitle].filter(Boolean).join(' · ');
		const draftOf = (question: AgentQuestion) => drafts[question.id] ?? EMPTY_DRAFT;
		const setDraft = (question: AgentQuestion, next: Draft) =>
			setDrafts((current) => ({...current, [question.id]: next}));

		const send = async () => {
			if (!answers || sending) return;
			setSending(true);
			try {
				await AgentAsks.answer(askId, answers);
			} catch (error) {
				ToastCommands.error(agentErrorMessage(i18n, error));
			} finally {
				setSending(false);
			}
		};

		const onlyQuestion = ask.questions[0]!;
		return (
			<article className={styles.card} data-flx="konvo-agents.ask-card" data-role={role} data-status={ask.status}>
				<div className={styles.cardHead}>
					{ask.provider && (
						<span className={styles.cardLogo}>
							<ProviderLogo provider={ask.provider} size={18} />
						</span>
					)}
					<span className={styles.cardTitle}>
						<Trans comment="Agents: title of a question card in a DM, e.g. JP's agent asks.">
							{owner}'s agent asks
						</Trans>
					</span>
					{meta && (
						<span className={styles.cardMeta} title={meta}>
							{meta}
						</span>
					)}
				</div>
				{ask.status === 'waiting' && (
					<div className={styles.cardWho} data-flx="konvo-agents.ask-card.who">
						{role === 'person' ? (
							<Trans comment="Agents: under the title of a question card a colleague got, e.g. Asked by JP for their agent.">
								Asked by {owner} for their agent · it carries on when you answer
							</Trans>
						) : (
							<Trans comment="Agents: the owner's own view of a question card they sent, e.g. You asked Lerato.">
								You asked {person} · their answer goes straight to the agent
							</Trans>
						)}
					</div>
				)}
				{ask.status === 'answered' && ask.answer && !single ? (
					<div className={styles.cardPairs}>
						{ask.answer.map((pair, i) => (
							<div key={i} className={questionStyles.pair}>
								<span className={questionStyles.pairQuestion}>{pair.question}</span>
								<span className={questionStyles.pairAnswer}>{pair.answer}</span>
							</div>
						))}
					</div>
				) : ask.status === 'waiting' ? (
					ask.questions.map((question) => (
						<QuestionBlock
							key={question.id}
							question={question}
							draft={draftOf(question)}
							onChange={(next) => setDraft(question, next)}
							editable={editable}
							showWritten={!single}
							busy={sending}
						/>
					))
				) : (
					<p className={questionStyles.question}>{onlyQuestion.question}</p>
				)}
				{editable && (
					<div className={clsx(styles.cardFoot, phone && styles.cardFootPhone)}>
						{single && onlyQuestion.allowCustomAnswer && (
							<WrittenAnswer
								question={onlyQuestion}
								draft={draftOf(onlyQuestion)}
								onChange={(next) => setDraft(onlyQuestion, next)}
								busy={sending}
								placeholder={t`Or write an answer…`}
							/>
						)}
						<button
							type="button"
							className={styles.cardSend}
							disabled={!answers || sending}
							onClick={() => void send()}
							data-flx="konvo-agents.ask-card.send"
						>
							{sending ? <RunningRing size={14} /> : <Trans>Send to the agent</Trans>}
						</button>
					</div>
				)}
				<StatusLine ask={ask} role={role} />
				{role === 'owner' && (
					<button
						type="button"
						className={styles.cardOpen}
						onClick={() => RouterUtils.transitionTo(Routes.agents(ask.machineId, ask.threadId))}
						data-flx="konvo-agents.ask-card.open"
					>
						<Trans comment="Agents: link on a question card the owner sent, to the agent's chat.">
							Open the agent chat
						</Trans>
					</button>
				)}
			</article>
		);
	},
);
